import { notificationKeys } from '@taler/api-client'
import { useInfiniteQuery, useMutation, useQueryClient } from '@tanstack/vue-query'
import { computed, type ComputedRef } from 'vue'
import { retrying } from '@/app/retrying'
import { useApi } from '@/app/api'
import type { Notification } from '../internal/types.ts'

export const NOTIFICATIONS_PAGE_SIZE = 25

export interface UseNotifications {
  readonly notifications: ComputedRef<readonly Notification[]>
  readonly isLoading: ComputedRef<boolean>
  readonly isFetchingNextPage: ComputedRef<boolean>
  readonly hasNextPage: ComputedRef<boolean>
  readonly error: ComputedRef<Error | null>
  readonly retrying: ComputedRef<boolean>
  fetchNextPage(): void
  retry(): void
  markRead(notificationId: string): void
}

export function useNotifications(): UseNotifications {
  const api = useApi()
  const queryClient = useQueryClient()

  const listQuery = useInfiniteQuery({
    queryKey: notificationKeys.list({ limit: NOTIFICATIONS_PAGE_SIZE }),
    queryFn: ({ pageParam, signal }) =>
      api.notifications.list(
        {
          limit: NOTIFICATIONS_PAGE_SIZE,
          ...(pageParam === undefined ? {} : { cursor: pageParam as string }),
        },
        signal,
      ),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) => lastPage.nextCursor,
    retry: false,
  })

  const markReadMutation = useMutation({
    mutationFn: (notificationId: string) => api.notifications.markRead(notificationId),
    onSuccess: () => {
      for (const queryKey of notificationKeys.invalidatedByRead()) {
        void queryClient.invalidateQueries({ queryKey })
      }
    },
  })

  return {
    notifications: computed(() => (listQuery.data.value?.pages ?? []).flatMap((page) => page.items)),
    isLoading: computed(() => listQuery.isPending.value),
    isFetchingNextPage: computed(() => listQuery.isFetchingNextPage.value),
    hasNextPage: computed(() => listQuery.hasNextPage.value),
    error: computed(() => listQuery.error.value),
    retrying: retrying(listQuery),
    fetchNextPage: () => {
      void listQuery.fetchNextPage()
    },
    retry: () => {
      void listQuery.refetch()
    },
    markRead: (notificationId) => markReadMutation.mutate(notificationId),
  }
}
